// client/src/context/AuthContext.jsx - מתוקן עם customFetch בלבד
import { createContext, useContext, useReducer, useEffect } from "react";
import { toast } from "react-toastify";
import customFetch from '../api/customFetch.js';

const AuthContext = createContext();

const initialAuthState = {
  user: null,
  isLoggedIn: false,
  loading: true,
  error: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case "AUTH_LOADING":
      return { ...state, loading: action.payload, error: null };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isLoggedIn: true,
        loading: false,
        error: null,
      };
    case "AUTH_ERROR":
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        loading: false,
        error: action.payload,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);

  // בדיקת סטטוס אימות
  const checkAuthStatus = async () => {
    try {
      dispatch({ type: "AUTH_LOADING", payload: true });

      const response = await customFetch.get('/users/check-auth');
      const data = response.data;

      if (data.success && data.isAuthenticated) {
        dispatch({ type: "AUTH_SUCCESS", payload: data.user });
      } else {
        dispatch({ type: "AUTH_ERROR", payload: null });
      }
    } catch (error) {
      console.error("שגיאה בבדיקת אימות:", error);
      dispatch({ type: "AUTH_ERROR", payload: "שגיאת רשת" });
    }
  };

  // התחברות
  const login = async (credentials) => {
    try {
      dispatch({ type: "AUTH_LOADING", payload: true });

      const response = await customFetch.post("/users/login", credentials);
      const data = response.data; // axios מחזיר data ישירות

      if (data.success) {
        dispatch({ type: "AUTH_SUCCESS", payload: data.user });
        toast.success("התחברת בהצלחה!");
        return { success: true };
      } else {
        dispatch({ type: "AUTH_ERROR", payload: data.message });
        toast.error(data.message || "שגיאה בהתחברות");
        return { success: false, message: data.message };
      }
    } catch (error) {
      // טיפול בשגיאות axios
      const message = error.response?.data?.message || "שגיאה בהתחברות";
      dispatch({ type: "AUTH_ERROR", payload: message });
      toast.error(message);
      return { success: false, message };
    }
  };

  // רישום
  const register = async (userData) => {
    try {
      const response = await customFetch.post("/users/register", userData);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "שגיאת רשת";
      return { success: false, message };
    }
  };

  // התנתקות
  const logout = async () => {
    try {
      await customFetch.post('/users/logout');
      toast.success("התנתקת בהצלחה!");
    } catch (error) {
      console.error("שגיאה בהתנתקות:", error);
    } finally {
      dispatch({ type: "AUTH_LOGOUT" });
    }
  };

  // ניקוי שגיאות
  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const contextValue = {
    ...state,
    login,
    register,
    logout,
    checkAuthStatus,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useAuthActions = () => {
  const { login, register, logout, clearError } = useAuth();
  return { login, register, logout, clearError };
};

export const useAuthState = () => {
  const { user, isLoggedIn, loading, error } = useAuth();
  return { user, isLoggedIn, loading, error };
};